import React from 'react';
import { Check } from 'lucide-react';
import type { Component, PricingComponent as PricingComponentType } from '../schemas/validation';
import { useThemeSafe } from '../contexts/ThemeContext';
import { useCustomColors, useBrandFonts } from '../contexts/CustomColorsContext';
import { getFontFamily } from '../utils/fontLoader';
import { getThemeStyles } from '../styles/themes';

interface PricingComponentProps {
  component: Component;
}

export const PricingComponent: React.FC<PricingComponentProps> = ({ component }) => {
  const { currentTheme } = useThemeSafe();
  const customColors = useCustomColors();
  const themeStyles = getThemeStyles(currentTheme, customColors || undefined);

  const brandFonts = useBrandFonts();

  // Font styles for headings and body text
  const headingFontStyle = brandFonts?.headingFont
    ? { fontFamily: getFontFamily(brandFonts.headingFont) }
    : {};
  const bodyFontStyle = brandFonts?.bodyFont
    ? { fontFamily: getFontFamily(brandFonts.bodyFont) }
    : {};

  console.log('🎨 PricingComponent: Using theme:', currentTheme, themeStyles.pricing);

  // Type guard for pricing component
  if (component.type !== 'pricing') {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 m-2">
        <p className="text-red-800">Invalid component type for pricing component</p>
      </div>
    );
  }

  // Now TypeScript knows this is a pricing component
  const { data } = component;
  const pricingData = data as PricingComponentType['data'];

  // Ensure data exists and has required properties
  if (!pricingData || !pricingData.plans || !Array.isArray(pricingData.plans)) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 m-2">
        <p className="text-red-800">Invalid pricing component data</p>
      </div>
    );
  }

  const {
    title,
    subtitle,
    description,
    plans,
    columns = '3',
    alignment = 'center',
    cardStyle = 'default',
    showDescription = true,
    showOriginalPrice = true,
    showBadges = true,
    cardShadow = true,
    cardBorder = false,
    backgroundColor,
    textColor,
    cardBackgroundColor,
    cardTextColor,
    accentColor,
    highlightBackgroundColor,
    highlightTextColor,
    showCurrency = true,
    currencyPosition = 'before',
    showPeriod = true,
    spacing = 'normal',
    hoverEffect = 'lift',
  } = pricingData;

  // Get grid columns class
  const getGridColumns = () => {
    switch (columns) {
      case '1':
        return 'grid-cols-1';
      case '2':
        return 'grid-cols-1 md:grid-cols-2';
      case '3':
        return 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3';
      case '4':
        return 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4';
      default:
        return 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3';
    }
  };

  // Get spacing classes
  const getSpacingClasses = () => {
    switch (spacing) {
      case 'compact':
        return 'py-8 gap-4';
      case 'normal':
        return 'py-12 gap-6';
      case 'spacious':
        return 'py-16 gap-8';
      default:
        return 'py-12 gap-6';
    }
  };

  // Get card style classes
  const getCardClasses = (plan: any) => {
    const baseClasses = 'relative flex flex-col h-full transition-all duration-300';
    const shadowClass = cardShadow ? 'shadow-lg' : '';
    const borderClass = cardBorder
      ? `border ${themeStyles.pricing?.borderColor || 'border-gray-200'}`
      : '';
    const hoverClass = getHoverEffectClass();
    const highlightClass = plan.highlighted
      ? `ring-2 ${themeStyles.pricing?.highlightRing || 'ring-blue-500'} transform scale-105`
      : '';
    const highlightedRadiusClass = cardStyle === 'rounded' ? 'rounded-xl' : 'rounded-lg';

    let styleClasses = '';
    switch (cardStyle) {
      case 'minimal':
        styleClasses = plan.highlighted
          ? `${themeStyles.pricing?.highlightedCard || 'bg-gradient-to-br from-blue-50 to-blue-100'} ${highlightedRadiusClass}`
          : `${themeStyles.pricing?.cardBg || 'bg-white'} rounded-none`;
        break;
      case 'bordered':
        styleClasses = plan.highlighted
          ? `${themeStyles.pricing?.highlightedCard || 'bg-gradient-to-br from-blue-50 to-blue-100'} ${highlightedRadiusClass} border-2 ${themeStyles.pricing?.highlightBorder || 'border-blue-300'}`
          : `${themeStyles.pricing?.cardBg || 'bg-white'} rounded-lg border-2 ${themeStyles.pricing?.borderColor || 'border-gray-200'}`;
        break;
      case 'rounded':
        styleClasses = plan.highlighted
          ? `${themeStyles.pricing?.highlightedCard || 'bg-gradient-to-br from-blue-50 to-blue-100'} ${highlightedRadiusClass}`
          : `${themeStyles.pricing?.cardBg || 'bg-white'} rounded-xl`;
        break;
      case 'modern':
        styleClasses = plan.highlighted
          ? `${themeStyles.pricing?.highlightedCard || 'bg-gradient-to-br from-blue-50 to-blue-100'} ${highlightedRadiusClass}`
          : `${themeStyles.pricing?.cardBg || 'bg-gradient-to-b from-white to-gray-50'} rounded-lg`;
        break;
      default:
        styleClasses = plan.highlighted
          ? `${themeStyles.pricing?.highlightedCard || 'bg-gradient-to-br from-blue-50 to-blue-100'} ${highlightedRadiusClass}`
          : `${themeStyles.pricing?.cardBg || 'bg-white'} rounded-lg`;
    }

    return `${baseClasses} ${styleClasses} ${shadowClass} ${borderClass} ${hoverClass} ${highlightClass}`.trim();
  };

  const getHoverEffectClass = () => {
    switch (hoverEffect) {
      case 'lift':
        return 'hover:transform hover:-translate-y-1 hover:shadow-xl';
      case 'zoom':
        return 'hover:transform hover:scale-105';
      case 'glow':
        return `hover:shadow-2xl ${themeStyles.pricing?.hoverGlow || 'hover:shadow-blue-500/25'}`;
      case 'none':
        return '';
      default:
        return 'hover:transform hover:-translate-y-1 hover:shadow-xl';
    }
  };

  // Format price
  const formatPrice = (plan: any) => {
    const { amount, currency, period, originalAmount } = plan.price;
    const currencySymbol =
      currency === 'USD' ? '$' : currency === 'EUR' ? '€' : currency === 'GBP' ? '£' : currency;

    const priceText = showCurrency
      ? currencyPosition === 'before'
        ? `${currencySymbol}${amount}`
        : `${amount}${currencySymbol}`
      : amount.toString();

    const periodText = showPeriod && period !== 'one-time' ? `/${period}` : '';

    return { priceText, periodText, originalAmount };
  };

  // Get alignment classes
  const getAlignmentClass = () => {
    switch (alignment) {
      case 'left':
        return 'text-left';
      case 'center':
        return 'text-center';
      case 'right':
        return 'text-right';
      default:
        return 'text-center';
    }
  };

  // Get colors with proper priority: data > theme > fallback
  // Use separate theme properties for different text elements, only use cardTextColor as last resort
  // Color priority: component.customColors > deprecated fields > theme colors
  const containerBgColor =
    pricingData.customColors?.background || backgroundColor || themeStyles.pricing?.background;
  const containerTextColor =
    pricingData.customColors?.text || textColor || themeStyles.pricing?.titleColor;
  const cardBg =
    pricingData.customColors?.cardBackground || cardBackgroundColor || themeStyles.pricing?.cardBg;
  const cardText =
    pricingData.customColors?.cardText ||
    cardTextColor ||
    themeStyles.pricing?.featureColor ||
    '#4B5563';
  const titleColor =
    pricingData.customColors?.text || textColor || themeStyles.pricing?.titleColor || '#111827';
  const priceColor =
    pricingData.customColors?.priceColor ||
    accentColor ||
    themeStyles.pricing?.priceColor ||
    '#DC2626';
  const featureColor =
    pricingData.customColors?.featureColor || themeStyles.pricing?.featureColor || '#4B5563';
  const checkColor =
    pricingData.customColors?.checkColor ||
    accentColor ||
    themeStyles.pricing?.checkColor ||
    '#DC2626';
  const highlightBg =
    pricingData.customColors?.highlightBackground ||
    highlightBackgroundColor ||
    themeStyles.pricing?.highlightedCard;
  const highlightText =
    pricingData.customColors?.highlightText ||
    highlightTextColor ||
    themeStyles.pricing?.highlightText ||
    '#ffffff';

  const containerStyle = {
    backgroundColor: containerBgColor,
    color: containerTextColor || undefined,
  };

  const getCardStyle = (plan: any) => ({
    backgroundColor: plan.highlighted ? highlightBg : cardBg,
    color: plan.highlighted ? highlightText : cardText,
  });

  return (
    <section
      className={`w-full ${getSpacingClasses()} px-4 ${themeStyles.pricing?.background || ''}`}
      style={containerStyle}
    >
      <div className="max-w-7xl mx-auto min-w-0">
        {/* Header */}
        {(title || subtitle || description) && (
          <div className="mb-12">
            <div
              className={`max-w-4xl ${alignment === 'center' ? 'mx-auto' : alignment === 'right' ? 'ml-auto' : ''}`}
            >
              {title && (
                <h2
                  className={`text-4xl font-bold mb-4 ${getAlignmentClass()}`}
                  style={{ color: titleColor, ...headingFontStyle }}
                >
                  {title}
                </h2>
              )}
              {subtitle && (
                <p
                  className={`text-xl mb-6 ${getAlignmentClass()}`}
                  style={{ color: featureColor, ...bodyFontStyle }}
                >
                  {subtitle}
                </p>
              )}
              {description && showDescription && (
                <p
                  className={`text-lg max-w-3xl ${getAlignmentClass()} ${alignment === 'center' ? 'mx-auto' : ''}`}
                  style={{ color: featureColor, ...bodyFontStyle }}
                >
                  {description}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Pricing Cards */}
        <div
          className={`grid ${getGridColumns()} ${getSpacingClasses().split(' ')[1]} [&>*]:min-w-0`}
        >
          {plans.map((plan, index) => {
            const { priceText, periodText, originalAmount } = formatPrice(plan);

            return (
              <div key={plan.id || index} className="min-w-0">
                <div className={`${getCardClasses(plan)} w-full`} style={getCardStyle(plan)}>
                  {/* Badge */}
                  {plan.highlighted && plan.highlightLabel && showBadges && (
                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                      <span
                        className="inline-block px-4 py-1 text-sm font-semibold rounded-full"
                        style={{
                          backgroundColor: priceColor,
                          color: '#ffffff',
                        }}
                      >
                        {plan.highlightLabel}
                      </span>
                    </div>
                  )}

                  {/* Custom Badge */}
                  {plan.badge && showBadges && (
                    <div className="absolute -top-3 right-4">
                      <span
                        className="inline-block px-3 py-1 text-xs font-semibold rounded-full"
                        style={{
                          backgroundColor: plan.badge.color || priceColor,
                          color: plan.badge.textColor || '#ffffff',
                        }}
                      >
                        {plan.badge.text}
                      </span>
                    </div>
                  )}

                  <div className="p-8 flex flex-col h-full">
                    {/* Plan Header */}
                    <div className="text-center mb-6">
                      <h3
                        className="text-2xl font-bold mb-2"
                        style={{
                          color: plan.highlighted ? highlightText : titleColor,
                          ...headingFontStyle,
                        }}
                      >
                        {plan.name}
                      </h3>
                      {plan.description && showDescription && (
                        <p
                          style={{
                            color: plan.highlighted ? highlightText : featureColor,
                            ...bodyFontStyle,
                          }}
                        >
                          {plan.description}
                        </p>
                      )}
                    </div>

                    {/* Pricing */}
                    <div className="text-center mb-8">
                      <div className="flex items-baseline justify-center mb-2">
                        <span
                          className="text-5xl font-bold"
                          style={{
                            color: plan.highlighted ? highlightText : priceColor,
                            ...bodyFontStyle,
                          }}
                        >
                          {priceText}
                        </span>
                        {periodText && (
                          <span
                            className="text-xl ml-1"
                            style={{
                              color: plan.highlighted ? highlightText : featureColor,
                              ...bodyFontStyle,
                            }}
                          >
                            {periodText}
                          </span>
                        )}
                      </div>
                      {originalAmount &&
                        showOriginalPrice &&
                        originalAmount > plan.price.amount && (
                          <div className="text-center">
                            <span
                              className="text-lg line-through"
                              style={{
                                color: plan.highlighted ? highlightText : featureColor,
                                opacity: 0.6,
                                ...bodyFontStyle,
                              }}
                            >
                              {showCurrency &&
                                currencyPosition === 'before' &&
                                `${formatPrice({ ...plan, price: { ...plan.price, amount: originalAmount } }).priceText.charAt(0)}`}
                              {originalAmount}
                              {showCurrency &&
                                currencyPosition === 'after' &&
                                `${formatPrice({ ...plan, price: { ...plan.price, amount: originalAmount } }).priceText.slice(-1)}`}
                            </span>
                          </div>
                        )}
                    </div>

                    {/* Features */}
                    <div className="flex-1 mb-8">
                      <ul className="space-y-3">
                        {plan.features.map((feature: string, featureIndex: number) => (
                          <li key={featureIndex} className="flex items-start">
                            <Check
                              size={20}
                              className="mr-3 mt-0.5 flex-shrink-0"
                              style={{ color: plan.highlighted ? highlightText : checkColor }}
                            />
                            <span
                              className="leading-relaxed"
                              style={{
                                color: plan.highlighted ? highlightText : featureColor,
                                ...bodyFontStyle,
                              }}
                            >
                              {feature}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* CTA Button */}
                    {plan.ctaButton && (
                      <div className="mt-auto">
                        <a
                          href={plan.ctaButton.url}
                          target={plan.ctaButton.target || '_self'}
                          className="w-full inline-flex items-center justify-center px-6 py-3 text-base font-medium rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 hover:opacity-90"
                          style={
                            plan.ctaButton.style === 'primary'
                              ? {
                                  backgroundColor: priceColor,
                                  color: '#ffffff',
                                  border: 'none',
                                }
                              : plan.ctaButton.style === 'secondary'
                                ? {
                                    backgroundColor: '#374151',
                                    color: '#ffffff',
                                    border: 'none',
                                  }
                                : plan.ctaButton.style === 'outline'
                                  ? {
                                      backgroundColor: 'transparent',
                                      color: priceColor,
                                      border: `2px solid ${priceColor}`,
                                    }
                                  : {
                                      backgroundColor: 'transparent',
                                      color: priceColor,
                                      border: 'none',
                                    }
                          }
                        >
                          {plan.ctaButton.text}
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
